import { useContext } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ApiContext } from '@shared/contexts';
import { invalidateRegistrationQueries } from '@modules/registrations/helpers/registrationQueries';

import { AccessType, TAccessInfo, TAccessNodeType } from './types';

export const useAccess = (id?: string, nodeType?: TAccessNodeType) => {
  // empty returns null data
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const getAccess = async (): Promise<TAccessInfo | null> => {
    if (!id || !nodeType) return null;
    return apiClient.get(`/access/${id}/${nodeType}`).then((res) => res.data);
  };

  const { data, isError, error } = useQuery({
    retry,
    queryKey: [`access-${id}`],
    queryFn: getAccess,
  });
  useHandleQueryError(isError, error, `Failed to load access data for ${id}!`, 'Loading access information failed!');

  return { error, data };
};

export const useUpdateAccess = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: {
    id: string;
    nodeType: TAccessNodeType;
    userId: string;
    accessType: AccessType;
  }): Promise<TAccessInfo> =>
    await apiClient
      .put(`/access/${variables.id}/${variables.nodeType}`, {
        accessType: variables.accessType,
        userId: variables.userId,
      })
      .then((res) => res.data);

  return useMutation({
    mutationFn,
    onSuccess: (data, variables) => {
      show('success', 'Access updated.');
      queryClient.setQueryData([`access-${variables.id}`], data);
      if (variables.nodeType === 'FOLDER') invalidateRegistrationQueries(queryClient);
    },
    onError: handleApiError('Updating access failed!'),
  });
};
